import React, { lazy, Suspense } from 'react';
import { Router, Route, Switch } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { hot } from 'react-hot-loader/root';

import LoadingPage from './Common/LoadingPage';
import LostPage from './LostPage';
import ScrollAndroidInputsToIntoViewIfNeeded from '../App/components/ScrollAndroidInputsIntoViewIfNeeded';
import TrTitle, { PageCategories } from '../common/TrTitle';

import { browserHistory } from '../services/history';
import { retry } from '../services/sharedUtils';

import '../styles/reset.css';
import '../styles/app_global.css';
import '../styles/root_styles.scss';

// Single routes, where user is performing all actions in the one component.
const ActivateAccount = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_ActivateAccount" */ './ActivateAccount')
));
const ConfirmPaymentPage = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_ConfirmPaymentPage" */ './Payment/ConfirmPayment/ConfirmPaymentPage')
));
const PreviewShareProfilePage = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_PreviewShareProfilePage" */ './ShareProfilePage/PreviewShareProfilePage')
));
const ShareProfilePage = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_ShareProfilePage" */ './ShareProfilePage/ShareProfilePage')
));
const UndergoingMaintenancePage = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_UndergoingMaintenance" */ './UndergoingMaintenance')
));

// Sub routers, where user is navigating between the routes in the router.
// Lazy import the whole router, so all the included routes will be loaded at once.
const AcceptDepositPage = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_AcceptDepositPage" */ './Deposit/AcceptDeposit/AcceptDepositPage')
));
const CommissionRoutes = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_Agent" */ './Agent/Routes')
));
const HaveYouFoundATenantRoutes = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_HaveYouFoundATenant" */ './HaveYouFoundATenant/Routes')
));
const RecommendationRoutes = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_Recommendation" */ './Recommendation/Routes')
));
const ResetPasswordRoutes = lazy(() => retry(() =>
  import(/* webpackChunkName: "Simple_ResetPassword" */ './ResetPassword/Routes')
));

function Routes() {
  return (
    <div style={{ minHeight: '100vh' }}>
      <TrTitle category={PageCategories.ROOT} name="Igglo" title="default" />

      <Switch>
        <Route exact path="/profile/activate" component={ActivateAccount} />

        <Route exact path="/profile/:shareId" component={ShareProfilePage} />
        <Route exact path="/preview_profile/:shareId" component={PreviewShareProfilePage} />

        <Route path="/reset_password" component={ResetPasswordRoutes} />
        <Route path="/password_changed" component={ResetPasswordRoutes} />

        <Route path="/commission" component={CommissionRoutes} />
        <Route path="/found_tenant" component={HaveYouFoundATenantRoutes} />
        <Route path="/recommend" component={RecommendationRoutes} />

        <Route
          path="/deposit/:depositId/accept/:acceptanceId"
          component={AcceptDepositPage}
        />

        <Route path="/payment/confirm/deposit/:depositId/:paymentId" component={ConfirmPaymentPage} />

        <Route path="/maintenance" component={UndergoingMaintenancePage} />

        <Route component={LostPage} />
      </Switch>
    </div>
  );
}

function SimpleSitesApp() {
  return (
    <Router history={browserHistory}>
      <ScrollAndroidInputsToIntoViewIfNeeded>
        <HelmetProvider>
          <Suspense fallback={<LoadingPage invertColors hideLink />}>
            <Routes />
          </Suspense>
        </HelmetProvider>
      </ScrollAndroidInputsToIntoViewIfNeeded>
    </Router>
  );
}

export default hot(SimpleSitesApp);
